import React, { useState, useCallback, useEffect, useRef } from 'react';
import { 
  Sparkles, Wand2, Check, X, ChevronDown, ChevronUp, 
  Lightbulb, AlertCircle, CheckCircle, Loader2, Copy,
  FileText, Zap, Star
} from 'lucide-react';
import { scriptEnhancerAPI } from '../services/api';

const ScriptEnhancer = ({ 
  value, 
  onChange, 
  fieldType = 'greeting', // 'greeting', 'system_prompt', 'conversational_flow', 'section'
  placeholder = 'Enter your script...',
  minHeight = '120px',
  maxLength = 2000,
  language = 'English',
  showTemplates = true,
  className = ''
}) => {
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [enhanceResult, setEnhanceResult] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [quickSuggestions, setQuickSuggestions] = useState([]);
  const [isLoadingQuick, setIsLoadingQuick] = useState(false);
  const [showTemplatePanel, setShowTemplatePanel] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [copied, setCopied] = useState(false);
  
  const debounceRef = useRef(null);
  const textareaRef = useRef(null);

  // Fetch templates on mount
  useEffect(() => {
    if (showTemplates) {
      fetchTemplates();
    }
  }, [showTemplates]);

  const fetchTemplates = async () => {
    try {
      const response = await scriptEnhancerAPI.getTemplateCategories();
      setTemplates(response.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  // Debounced quick suggestions
  const getQuickSuggestions = useCallback(async (text) => {
    if (!text || text.length < 20) {
      setQuickSuggestions([]);
      return;
    }

    setIsLoadingQuick(true);
    try {
      const response = await scriptEnhancerAPI.quickSuggest({
        text,
        field_type: fieldType
      });
      setQuickSuggestions(response.data.suggestions || []);
    } catch (error) {
      console.error('Quick suggestions error:', error);
      setQuickSuggestions([]);
    } finally {
      setIsLoadingQuick(false);
    }
  }, [fieldType]);

  // Handle text change with debounced suggestions
  const handleTextChange = (e) => {
    const newValue = e.target.value.slice(0, maxLength);
    onChange(newValue);

    // Clear previous timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Debounce quick suggestions (1.5 seconds after typing stops)
    debounceRef.current = setTimeout(() => {
      getQuickSuggestions(newValue);
    }, 1500);
  };

  // Enhance script with AI
  const handleEnhance = async () => {
    if (!value.trim()) return;

    setIsEnhancing(true);
    setEnhanceResult(null);
    setShowSuggestions(true);

    try {
      const response = await scriptEnhancerAPI.enhance({
        text: value,
        field_type: fieldType,
        language
      });
      setEnhanceResult(response.data);
    } catch (error) {
      console.error('Enhancement error:', error);
      setEnhanceResult({ error: 'Failed to enhance script. Please try again.' });
    } finally {
      setIsEnhancing(false);
    }
  };

  // Apply enhanced text
  const applyEnhancement = () => {
    if (enhanceResult?.enhanced_text) {
      onChange(enhanceResult.enhanced_text);
      setShowSuggestions(false);
      setEnhanceResult(null);
    }
  };

  // Apply a quick suggestion
  const applyQuickSuggestion = (suggestion) => {
    const newText = value + ' ' + suggestion;
    onChange(newText.slice(0, maxLength));
    setQuickSuggestions([]);
  };

  // Apply template
  const applyTemplate = async (categoryId, field) => {
    try {
      const response = await scriptEnhancerAPI.getTemplate(categoryId);
      const template = response.data;
      
      if (field === 'greeting' && template.greeting) {
        onChange(template.greeting);
      } else if (field === 'system_prompt' && template.system_prompt) {
        onChange(template.system_prompt);
      }
      
      setShowTemplatePanel(false);
    } catch (error) {
      console.error('Failed to load template:', error);
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-400 bg-red-400/10';
      case 'medium': return 'text-yellow-400 bg-yellow-400/10';
      case 'low': return 'text-green-400 bg-green-400/10';
      default: return 'text-gray-400 bg-gray-400/10';
    }
  };

  // Get suggestion type icon
  const getSuggestionIcon = (type) => {
    switch (type) {
      case 'improvement': return <Zap className="w-4 h-4 text-blue-400" />;
      case 'missing': return <AlertCircle className="w-4 h-4 text-orange-400" />;
      case 'tone': return <Star className="w-4 h-4 text-purple-400" />;
      case 'clarity': return <Lightbulb className="w-4 h-4 text-yellow-400" />;
      default: return <Lightbulb className="w-4 h-4 text-gray-400" />;
    }
  };

  return (
    <div className={`relative ${className}`}>
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {/* Enhance Button */}
          <button
            onClick={handleEnhance}
            disabled={isEnhancing || !value.trim()}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
              isEnhancing || !value.trim()
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600 shadow-lg shadow-purple-500/25'
            }`}
            data-testid="enhance-script-btn"
          >
            {isEnhancing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Enhancing...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Enhance with AI
              </>
            )}
          </button>

          {/* Templates Button */}
          {showTemplates && (
            <button
              onClick={() => setShowTemplatePanel(!showTemplatePanel)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-700 text-gray-300 rounded-lg text-sm hover:bg-gray-600 transition-colors"
              data-testid="templates-btn"
            >
              <FileText className="w-4 h-4" />
              Templates
              {showTemplatePanel ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          )}
        </div>

        {/* Character count */}
        <span className="text-xs text-gray-500">
          {value.length}/{maxLength}
        </span>
      </div>

      {/* Template Panel */}
      {showTemplatePanel && (
        <div className="mb-3 p-3 bg-gray-800 border border-gray-700 rounded-lg">
          <p className="text-sm text-gray-400 mb-2">Choose a template to start:</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {templates.map(template => (
              <button
                key={template.id}
                onClick={() => applyTemplate(template.id, fieldType === 'system_prompt' ? 'system_prompt' : 'greeting')}
                className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-left transition-colors"
              >
                <p className="text-sm text-white font-medium">{template.name}</p>
                <p className="text-xs text-gray-400 truncate">{template.description}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Main Textarea */}
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleTextChange}
          placeholder={placeholder}
          className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg p-4 text-white placeholder-gray-500 outline-none focus:border-purple-400 resize-none transition-colors"
          style={{ minHeight }}
          data-testid="script-textarea"
        />

        {/* Quick Suggestions Popup */}
        {quickSuggestions.length > 0 && (
          <div className="absolute bottom-full left-0 right-0 mb-2 p-3 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-10">
            <div className="flex items-center gap-2 mb-2">
              <Lightbulb className="w-4 h-4 text-yellow-400" />
              <span className="text-sm text-gray-300">Quick suggestions:</span>
              {isLoadingQuick && <Loader2 className="w-3 h-3 animate-spin text-gray-400" />}
            </div>
            <div className="flex flex-wrap gap-2">
              {quickSuggestions.map((suggestion, idx) => (
                <button
                  key={idx}
                  onClick={() => applyQuickSuggestion(suggestion)}
                  className="px-2 py-1 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded text-xs transition-colors"
                >
                  + {suggestion.slice(0, 50)}{suggestion.length > 50 ? '...' : ''}
                </button>
              ))}
              <button
                onClick={() => setQuickSuggestions([])}
                className="p-1 text-gray-500 hover:text-gray-300"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Enhancement Results Panel */}
      {showSuggestions && enhanceResult && (
        <div className="mt-4 bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 bg-gray-900/50 border-b border-gray-700">
            <div className="flex items-center gap-3">
              <Wand2 className="w-5 h-5 text-purple-400" />
              <span className="text-white font-medium">AI Enhancement Results</span>
              {enhanceResult.score && (
                <div className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  enhanceResult.score >= 80 ? 'bg-green-400/10 text-green-400' :
                  enhanceResult.score >= 60 ? 'bg-yellow-400/10 text-yellow-400' :
                  'bg-red-400/10 text-red-400'
                }`}>
                  Score: {enhanceResult.score}/100
                </div>
              )}
            </div>
            <button
              onClick={() => setShowSuggestions(false)}
              className="p-1 text-gray-500 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {enhanceResult.error ? (
            <div className="p-4 text-red-400 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              {enhanceResult.error}
            </div>
          ) : (
            <>
              {/* Enhanced Text Preview */}
              <div className="p-4 border-b border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400">Enhanced Version:</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => copyToClipboard(enhanceResult.enhanced_text)}
                      className="p-1.5 text-gray-500 hover:text-white transition-colors"
                      title="Copy"
                    >
                      {copied ? <CheckCircle className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="p-3 bg-gray-900 rounded-lg text-gray-300 text-sm whitespace-pre-wrap max-h-48 overflow-y-auto">
                  {enhanceResult.enhanced_text}
                </div>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={applyEnhancement}
                    className="flex items-center gap-1.5 px-4 py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 transition-colors"
                    data-testid="apply-enhancement-btn"
                  >
                    <Check className="w-4 h-4" />
                    Apply Enhancement
                  </button>
                  <button
                    onClick={() => setShowSuggestions(false)}
                    className="flex items-center gap-1.5 px-4 py-2 bg-gray-700 text-gray-300 rounded-lg text-sm hover:bg-gray-600 transition-colors"
                  >
                    <X className="w-4 h-4" />
                    Keep Original
                  </button>
                </div>
              </div>

              {/* Suggestions List */}
              {enhanceResult.suggestions?.length > 0 && (
                <div className="p-4 border-b border-gray-700">
                  <h4 className="text-sm text-gray-400 mb-3">Suggestions:</h4>
                  <div className="space-y-2">
                    {enhanceResult.suggestions.map((suggestion, idx) => (
                      <div 
                        key={idx}
                        className="p-3 bg-gray-900/50 rounded-lg"
                      >
                        <div className="flex items-start gap-2">
                          {getSuggestionIcon(suggestion.type)}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs text-gray-500 uppercase">{suggestion.type}</span>
                              <span className={`px-1.5 py-0.5 rounded text-xs ${getPriorityColor(suggestion.priority)}`}>
                                {suggestion.priority}
                              </span>
                            </div>
                            <p className="text-gray-300 text-sm">{suggestion.suggested}</p>
                            {suggestion.reason && (
                              <p className="text-gray-500 text-xs mt-1">{suggestion.reason}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Missing Elements */}
              {enhanceResult.missing_elements?.length > 0 && (
                <div className="p-4">
                  <h4 className="text-sm text-gray-400 mb-3">Missing Elements:</h4>
                  <div className="flex flex-wrap gap-2">
                    {enhanceResult.missing_elements.map((element, idx) => (
                      <span 
                        key={idx}
                        className="px-2 py-1 bg-orange-400/10 text-orange-400 rounded text-xs"
                      >
                        {element}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ScriptEnhancer;
